import 'classlist-polyfill';

import {formatTime} from './utils/util';
import Hls          from 'hls.js';
import Config       from './config';

const playerTemplate = require('html-loader!./html/player.html');

class HlsPlayer
{
    static version = __VERSION__;

    #config;

    #container;

    #video;
    #volume;

    #hls;

    #playerBlockElement;
    #playBigButtonElement;
    #playButtonElement;
    #fullScreenBtnElement;
    #currentTimeElement;
    #durationTimeElement;
    #progressLineWrapElement;
    #progressLineElement;
    #progressLineCurrentElement;
    #progressLineBufferElement;
    #volumeSliderWrapElement;
    #volumeSliderElement;
    #volumeButtonElement;
    #qualityButtonElement;
    #qualityListElement;
    #subtitleBtnElement;
    #subtitleListElement;
    #subtitleIconElement;

    #controlBarTimeout;

    get version()
    {
        return __VERSION__;
    }

    constructor(config)
    {
        try {
            this.#config = new Config(config);
        } catch (error) {
            console.error(error.message);
            return;
        }

        this.#container = this.#config.container;

        switch (true) {
            case !Hls.isSupported():
                console.error(`HLS is not supported!`);
                this.#renderNative();
                break;

            case this.#config.nativeControls:
                this.#renderNative();
                break;

            default:
                this.#renderCustom();
                this.#initializeContainerElements();
                this.#initializePlayer();
                break;
        }
    }

    #renderNative()
    {
        let video = document.createElement(`video`);
        video.setAttribute(`src`, this.#config.src);

        if (this.#config.poster) {
            video.setAttribute(`poster`, this.#config.poster);
        }

        if (!this.#config.autoStartLoad) {
            video.setAttribute(`preload`, `metadata`);
        }

        video.setAttribute(`tabindex`, `-1`);
        video.setAttribute(`height`, `100%`);
        video.setAttribute(`width`, `100%`);
        video.setAttribute(`controls`, ``);

        this.#container.appendChild(video);
    }

    #renderCustom()
    {
        this.#container.classList.add(`playernow-default-skin`);
        this.#container.innerHTML = playerTemplate;
    }

    #initializeContainerElements()
    {
        this.#video = this.#container.querySelector(`.playernow-video-el`);

        this.#playerBlockElement = this.#container.querySelector(`.playernow-block`);

        this.#playBigButtonElement = this.#container.querySelector(`.playernow-play-button`);
        this.#playButtonElement = this.#container.querySelector(`button[rel="play"]`);
        this.#fullScreenBtnElement = this.#container.querySelector(`.playernow-fullscreen-enter`);

        this.#currentTimeElement = this.#container.querySelector(`.playernow-time-current`);
        this.#durationTimeElement = this.#container.querySelector(`.playernow-time-duration`);

        this.#progressLineWrapElement = this.#container.querySelector(`.playernow-progress-wrap`);
        this.#progressLineElement = this.#container.querySelector(`.playernow-progress-line`);
        this.#progressLineCurrentElement = this.#container.querySelector(`.playernow-progress-current`);
        this.#progressLineBufferElement = this.#container.querySelector(`.playernow-progress-buffer`);

        this.#volumeSliderWrapElement = this.#container.querySelector(`.playernow-slider-wrap`);
        this.#volumeSliderElement = this.#container.querySelector(`.playernow-slider-range-value`);
        this.#volumeButtonElement = this.#container.querySelector(`.playernow-volume-button`);

        this.#qualityButtonElement = this.#container.querySelector(`.playernow-action-block.playernow-quality-wrap`);
        this.#qualityListElement = this.#container.querySelector(`.playernow-quality-list`);

        this.#subtitleBtnElement = this.#container.querySelector(`.playernow-action-block.playernow-subtitle-wrap`);
        this.#subtitleListElement = this.#container.querySelector(`.playernow-subtitle-list`);
        this.#subtitleIconElement = this.#container.querySelector(`.playernow-subtitle-icon`);
    }

    #initializePlayer()
    {
        this.#volume = this.#video.volume;

        this.#video.setAttribute(`src`, this.#config.src);
        if (this.#config.poster) {
            this.#video.setAttribute(`poster`, this.#config.poster);
        }

        this.#initializeHls();

        this.#addEventListeners();

        this.#setProgressLine(0, 0);
    }

    #initializeHls()
    {
        this.#hls = new Hls(this.#config.hlsConfig);

        this.#hls.attachMedia(this.#video);

        this.#hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            this.#hls.loadSource(this.#config.src);

            let once = true;
            this.#hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
                if (once) {
                    this.#setDefaultQuality();
                    this.#setDefaultSubtitle();

                    if (this.#config.autoplay) {
                        this.#video.play()
                            .catch(error => {
                                console.info(`Can't autoplay video`);
                            });
                    }

                    once = false;
                }
            });

            this.#hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                console.log(`manifest loaded, found ${data.levels.length} quality levels`);

                const qualityTracksLength = this.#hls.levels.length;
                if (qualityTracksLength <= 1) {
                    return false;
                }

                this.#qualityButtonElement.classList.remove(`hide`);
                this.#addQuality(`auto`, -1);

                for (let i = 0; i < qualityTracksLength; i++) {
                    const name = this.#hls.levels[i].height;
                    this.#addQuality(name, i);
                }
            });

            this.#hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (event, data) => {
                console.log(`manifest loaded, found ${data.subtitleTracks.length} subtitle tracks`);

                const subtitleTracksLength = data.subtitleTracks.length;
                if (subtitleTracksLength <= 1) {
                    return false;
                }

                this.#subtitleBtnElement.classList.remove(`hide`);
                this.#addSubtitle(`disable`, -1);
                for (let s = 0; s < subtitleTracksLength; s++) {
                    const subtitle = data.subtitleTracks[s];
                    this.#addSubtitle(subtitle.name, subtitle.id);
                }
            });
        });
    }

    #addEventListeners()
    {
        this.#video.addEventListener(`click`, () => this.#onPlayBtn());

        this.#video.addEventListener(`pause`, () => this.#onPauseVideo());

        this.#video.addEventListener(`play`, () => this.#onPlayVideo());

        this.#video.addEventListener(`timeupdate`, () => {
            this.#setCurrentTime(this.#video.currentTime);
            const bufferTime = this.#video.buffered.length > 0 ? this.#video.buffered.end(0) : 0;
            this.#setProgressLine(this.#video.currentTime, bufferTime);
            this.#updateDurationTime();//todo
        });

        this.#video.addEventListener(`ended`, () => {
            this.#playButtonElement.classList.add(`playernow-play-button`);
            this.#playButtonElement.classList.remove(`playernow-pause-button`)
        });

        this.#playBigButtonElement.addEventListener(`click`, () => this.#onPlayBtn());

        this.#playButtonElement.addEventListener(`click`, () => this.#onPlayBtn());

        this.#fullScreenBtnElement.addEventListener(`click`, () => this.#onFullScreen(!this.#isFullScreen()));

        [`webkitfullscreenchange`, `fullscreenchange`].forEach(eventName => {
            document.addEventListener(eventName, () => {
                if (this.#isFullScreen()) {
                    return false;
                }

                this.#onFullScreen(false);
            });
        });

        this.#progressLineWrapElement.addEventListener(`click`, event => {
            const seconds = this.#getPositionProgressLineFromEvent(event);
            const bufferTime = this.#video.buffered.length > 0 ? this.#video.buffered.end(0) : 0;

            this.#setProgressLine(seconds, bufferTime);
            this.#setCurrentTime(seconds);

            this.#video.currentTime = seconds;

        });

        this.#volumeButtonElement.addEventListener(`mousedown`, () => {
            this.#setMute(this.#volumeButtonElement.classList.contains(`playernow-volume-mute`));
            this.#updateVolumeLine();
        });

        this.#volumeSliderWrapElement.addEventListener(`mousedown`, event => {
            this.#onSliderMouseDown(event);
            this.#updateVolumeLine();
        });

        this.#qualityButtonElement.addEventListener(`click`, () => this.#onQualityBtn());

        this.#subtitleBtnElement.addEventListener(`click`, () => this.#onSubtitleBtn());

        this.#playerBlockElement.addEventListener(`mouseenter`, () => this.#onMouseOver());
        this.#playerBlockElement.addEventListener(`mousemove`, () => this.#onMouseOver());

        this.#playerBlockElement.addEventListener(`mouseleave`, () => this.#onMouseLeave());
    }

    #setDefaultQuality()
    {
        if (this.#config.defaultQualityHeight) {
            const defaultQuality = this.#hls.levels.find(subtitle => {
                return subtitle.height === this.#config.defaultQualityHeight;
            });

            if (defaultQuality) {
                const defaultQualityElement = this.#qualityListElement.querySelector(`[value="${this.#hls.levels.indexOf(defaultQuality)}"]`);
                if (defaultQualityElement) {
                    this.#onQualityTrackClick(defaultQualityElement);
                }
            }
        }
    }

    #setDefaultSubtitle()
    {
        if (this.#config.defaultSubtitleLang) {
            const defaultSubtitle = this.#hls.subtitleTracks.find(subtitle => {
                return subtitle.lang === this.#config.defaultSubtitleLang;
            });

            if (defaultSubtitle) {
                const defaultSubtitleElement = this.#subtitleListElement.querySelector(`[value="${defaultSubtitle.id}"]`);
                if (defaultSubtitleElement) {
                    this.#onSubtitleTrackClick(defaultSubtitleElement);
                }
            }
        }
    }

    #onMouseOver()
    {
        clearTimeout(this.#controlBarTimeout);
        this.#showControlBar();
        this.#controlBarTimeout = setTimeout(() => this.#onMouseLeave(), 3000);
    }

    #onMouseLeave()
    {
        clearTimeout(this.#controlBarTimeout);
        if (this.#video.paused) {
            this.#showControlBar();
        } else {
            this.#hideControlBar();
        }
    }

    #showControlBar()
    {
        this.#playerBlockElement.classList.remove(`playernow-control-bar-hidden`);
    }

    #hideControlBar()
    {
        this.#playerBlockElement.classList.add(`playernow-control-bar-hidden`);
    }

    #onSliderMouseDown(event)
    {
        event.preventDefault();
        const handle = this.#volumeSliderWrapElement;

        const moveAt = event => {
            const offsetLeft = handle.getBoundingClientRect().left;
            const width = handle.getBoundingClientRect().right - handle.getBoundingClientRect().left;
            const left = event.clientX - offsetLeft;
            const current = Math.min(100, Math.max(0, (left/width)*100));

            if (current/100 === 0) {
                this.#setMute(true);
            } else {
                this.#setMute(false);
                this.#video.volume = (current/100);
            }
            this.#updateVolumeLine();
        };

        const onMouseMove = event => moveAt(event);

        document.addEventListener(`mousemove`, onMouseMove);
        document.addEventListener(`mouseup`, () => document.removeEventListener(`mousemove`, onMouseMove));

        moveAt(event);
    }

    #setMute(mute)
    {
        if (mute) {
            this.#volume = this.#video.volume;
            this.#video.volume = 0;
            this.#volumeButtonElement.classList.remove(`playernow-volume-mute`);
            this.#volumeButtonElement.classList.add(`playernow-volume-unmute`);
        } else {
            this.#video.volume = this.#volume;
            this.#volumeButtonElement.classList.add(`playernow-volume-mute`);
            this.#volumeButtonElement.classList.remove(`playernow-volume-unmute`);
        }
    }

    #updateVolumeLine()
    {
        this.#volumeSliderElement.style.width = `${this.#video.volume*100}%`;
    }

    #setProgressLine(currentTime, bufferTime)
    {
        const duration = this.#video.duration;
        let currentTimeWidth = 0;

        if (currentTime !== 0 && duration) {
            currentTimeWidth = ((currentTime*100)/duration);
        }

        let bufferTimeWidth = 0;
        if (bufferTime !== 0 && duration) {
            bufferTimeWidth = ((bufferTime*100)/duration);
        }

        this.#progressLineCurrentElement.style.width = `${currentTimeWidth}%`;
        this.#progressLineBufferElement.style.width = `${bufferTimeWidth}%`;
    }

    #setCurrentTime(currentTime)
    {
        this.#currentTimeElement.innerHTML = formatTime(currentTime);
    }

    #updateDurationTime()
    {
        if (!this.#video.duration) {
            return false;
        }

        this.#durationTimeElement.innerHTML = formatTime(this.#video.duration);
    }

    #getPositionProgressLineFromEvent(event)
    {
        const offset = this.#progressLineElement.getBoundingClientRect().left;
        const left = event.clientX - offset;

        let seconds = this.#video.duration*left/this.#progressLineElement.clientWidth;
        if (seconds < 0) {
            seconds = 0;
        }

        return seconds;
    }

    #onPlayBtn()
    {
        this.#onMouseOver();

        if (this.#playButtonElement.classList.contains(`playernow-play-button`)) {
            if (!this.#config.autoStartLoad && typeof this.#hls.startPosition === `undefined`) {
                this.#hls.startPosition = -1;
                this.#hls.startLoad(-1);
            }

            this.#video.play();
            this.#onPlayVideo();
        } else {
            this.#video.pause();
            this.#onPauseVideo();
        }
    }

    #onPauseVideo()
    {
        this.#playButtonElement.classList.add(`playernow-play-button`);
        this.#playButtonElement.classList.remove(`playernow-pause-button`);
        this.#playBigButtonElement.classList.remove(`hide`);
    }

    #onPlayVideo()
    {
        this.#playButtonElement.classList.add(`playernow-pause-button`);
        this.#playButtonElement.classList.remove(`playernow-play-button`);
        this.#playBigButtonElement.classList.add(`hide`);
    }

    #isFullScreen()
    {
        return [
            `fullscreenElement`,
            `msFullscreenElement`,
            `webkitFullscreenElement`,
            `webkitIsFullScreen`,
            `webkitDisplayingFullscreen`,
            `mozFullScreenElement`,
        ]
            .some(property => property in document && document[property]);
    }

    #onFullScreen(fullScreen)
    {
        if (fullScreen) {
            this.#fullScreenBtnElement.classList.remove(`playernow-fullscreen-enter`);
            this.#fullScreenBtnElement.classList.add(`playernow-fullscreen-exit`);
            this.#playerBlockElement.classList.add(`playernow-fullscreen`);
        } else {
            this.#fullScreenBtnElement.classList.remove(`playernow-fullscreen-exit`);
            this.#fullScreenBtnElement.classList.add(`playernow-fullscreen-enter`);
            this.#playerBlockElement.classList.remove(`playernow-fullscreen`);
        }

        if (fullScreen) {
            if (`requestFullscreen` in this.#container) {
                this.#container.requestFullscreen();
            } else if (`msRequestFullscreen` in this.#container) {
                this.#container.msRequestFullscreen();
            } else if (`mozRequestFullScreen` in this.#container) {
                this.#container.mozRequestFullScreen();
            } else if (`webkitRequestFullscreen` in this.#container) {
                this.#container.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
            } else if (`webkitEnterFullscreen` in this.#container) {
                this.#container.webkitEnterFullscreen();
            }
        } else if (`exitFullscreen` in document) {
            document.exitFullscreen();
        } else if (`msExitFullscreen` in document) {
            document.msExitFullscreen();
        } else if (`mozCancelFullScreen` in document) {
            document.mozCancelFullScreen();
        } else if (`webkitExitFullscreen` in document) {
            document.webkitExitFullscreen();
        } else if (`webkitExitFullscreen` in this.#container) {
            this.#container.webkitExitFullscreen();
        }
    }

    #onQualityTrackClick(element)
    {
        Array.from(this.#qualityListElement.querySelectorAll(`li`)).forEach(li => li.classList.remove(`active`));

        element.classList.add(`active`);

        this.#setQuality(element.innerText, element.value);
    }

    #addQuality(qualityName, qualityLevel)
    {
        let qualityLi = document.createElement(`li`);

        qualityLi.classList.add(`playernow-quality`);
        qualityLi.innerText = qualityName;
        qualityLi.value = qualityLevel;

        qualityLi.addEventListener(`click`, event => this.#onQualityTrackClick(event.target));

        this.#qualityListElement.appendChild(qualityLi);
    }

    #setQuality(qualityName, qualityLevel)
    {
        this.#hls.currentLevel = qualityLevel;
    }

    #onQualityBtn()
    {
        this.#subtitleBtnElement.classList.remove(`active`);
        this.#qualityButtonElement.classList.toggle(`active`);
    }

    #addSubtitle(subtitleName, subtitleLevel)
    {
        let subtitleLi = document.createElement(`li`);

        subtitleLi.classList.add(`playernow-subtitle`);
        subtitleLi.innerText = subtitleName;
        subtitleLi.value = subtitleLevel;

        subtitleLi.addEventListener(`click`, event => this.#onSubtitleTrackClick(event.target));

        this.#subtitleListElement.appendChild(subtitleLi);
    }

    #onSubtitleTrackClick(element)
    {
        Array.from(this.#subtitleListElement.querySelectorAll(`li`)).forEach(li => li.classList.remove(`active`));

        element.classList.add(`active`);

        this.#setSubtitle(element.innerText, element.value);
    }

    #setSubtitle(subtitleName, subtitleLevel)
    {
        if (subtitleLevel < 0) {
            this.#subtitleIconElement.classList.remove(`chosen`);
        } else {
            this.#subtitleIconElement.classList.add(`chosen`);
        }

        this.#hls.subtitleTrack = subtitleLevel;
    }

    #onSubtitleBtn()
    {
        this.#qualityButtonElement.classList.remove(`active`);
        this.#subtitleBtnElement.classList.toggle(`active`);
    }
}

window.HlsPlayer = HlsPlayer;

export default HlsPlayer;